import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connectLean } from 'lean-redux';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';
import dayjs from 'dayjs';
import Button from 'chameleon/ui-stack/ui/base/button';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import TextWithTooltip from 'chameleon/ui-stack/ui/assessments/text-with-tooltip';
import { SPPPerformanceBar } from 'chameleon/ui-stack/ui/assessments/spp-progress-bar';
import get from 'lodash/get';
import PracticeProblems from './PracticeProblems';
import StudentViewAll from '../StudentViewAll';

const StudentPracticeProblems = (props) => {
  const { resources, unitsStatuses, unitId, params, router } = props;
  const actionButtonRef = useRef({});

  const resourceStatuses = get(unitsStatuses, `unit-${unitId}.resources`, {});
  const { location } = router || {};
  const { subject } = params;

  const getSppAssignments = (resourceId, assessmentId) => {
    const { sppAssignments } =
      resourceStatuses[`resource-${resourceId}-subject-${subject}`] || {};

    return get(sppAssignments, assessmentId, []);
  };

  const getAssignmentUrl = (assignment) => {
    if (!assignment) {
      return null;
    }

    return `/${subject}/assessments/spp/${get(assignment, 'id', 0)}`;
  };

  const getAssignedIcon = (
    id,
    learnosityActivityId = '',
    resourceId = null,
  ) => {
    if ((learnosityActivityId || '').startsWith('SPP_SANDBOX')) {
      return null;
    }

    const sppAssignments = resourceId ? getSppAssignments(resourceId, id) : [];
    if (sppAssignments.length === 1) {
      return null;
    }

    const tooltipTargetId = `${id}-assigned-icon`;

    return (
      <TextWithTooltip
        text={
          <span id={tooltipTargetId} className="align-middle mb-0 text-12">
            <Icon
              id="person_check_assigned"
              width="20"
              className="align-middle text-gray-700"
              accessibility={{
                ariaLabel: 'Assigned',
              }}
            />
          </span>
        }
        target={tooltipTargetId}
        tooltip="Assigned"
      />
    );
  };

  const getFullyAnsweredIcon = (
    id,
    learnosityActivityId = '',
    decimalScore = 0,
  ) => {
    if (
      !(learnosityActivityId || '').startsWith('SPP_SANDBOX') ||
      parseInt(decimalScore, 10) !== 1
    ) {
      return null;
    }

    const tooltipTargetId = `${id}-fully-answered-icon`;
    const toolTipText = 'Fully Answered';

    return (
      <TextWithTooltip
        text={
          <span id={tooltipTargetId} className="align-middle mb-0 text-12">
            <Icon
              id="fully-answered"
              width="20"
              className="align-middle text-black"
              accessibility={{
                ariaLabel: toolTipText,
              }}
            />
          </span>
        }
        target={tooltipTargetId}
        tooltip={toolTipText}
      />
    );
  };

  const getIcons = (id, learnosityActivityId, resourceId, decimalScore = 0) => (
    <Fragment>
      {getFullyAnsweredIcon(id, learnosityActivityId, decimalScore)}
      {resourceId
        ? getAssignedIcon(id, learnosityActivityId, resourceId)
        : null}
    </Fragment>
  );

  const getSingleAssignmentAction = (assignment) => {
    const headline = get(assignment, 'headline', '');
    const assessmentId = get(assignment, 'assessmentId', '');
    const assignmentId = get(assignment, 'id', '');
    const learnosityActivityId = get(assignment, 'learnosityActivityId', '');
    const isSPPSandbox = (learnosityActivityId || '').startsWith('SPP_SANDBOX');
    const score = get(assignment, 'score', 0) || 0;
    const maxScore = get(assignment, 'maxScore', 0) || 0;
    const decimalScore = maxScore ? score / maxScore : 0;
    const isFullyAnsweredSandbox =
      parseInt(decimalScore, 10) === 1 && isSPPSandbox;
    let titleCaseButtonText =
      headline[0].toUpperCase() + headline.slice(1).toLowerCase();
    /**
     * For the Sandbox add a 'Practice' suffix to the button label to reenforce the
     * difference between opening a teacher assigned vs a sandbox assignment
     */
    titleCaseButtonText = isFullyAnsweredSandbox
      ? 'Review'
      : isSPPSandbox
        ? `${titleCaseButtonText} Practice`
        : titleCaseButtonText;
    const icons = getIcons(
      assignmentId,
      learnosityActivityId,
      null,
      decimalScore,
    );
    const link = get(assignment, 'link', '');
    const isSubmitted = !!get(assignment, 'submittedAt', 0);
    const isFullySubmitted = !!get(assignment, 'learnositySubmittedAt', 0);
    const isSubmittedOrMarkedComplete =
      isSubmitted ||
      ['closed', 'locked'].includes(get(assignment, 'status', ''));

    return (
      <Fragment>
        {icons}
        {!isSubmittedOrMarkedComplete ? (
          <Button
            isPrimary
            tabIndex={0}
            accessibility={{ 'aria-label': titleCaseButtonText }}
            className="ml-4"
            onClick={(event) =>
              onResourceClicked(
                event,
                getAssignmentUrl(assignment),
                actionButtonRef.current[assessmentId],
              )
            }
          >
            {titleCaseButtonText}
          </Button>
        ) : isFullySubmitted ? (
          <SPPPerformanceBar
            spp={{
              ...assignment,
              progress: { started: 0 },
            }}
            studentScore={{
              percentCorrect: Math.round(decimalScore * 1000) / 10,
            }}
            customClasses="pr-5 pl-5 font-medium text-12 cursor-pointer"
            onClick={(event) =>
              onResourceClicked(
                event,
                `/${link}`,
                actionButtonRef.current[assessmentId],
              )
            }
            role="link"
          />
        ) : (
          <div className="flex items-center py-3">No Results</div>
        )}
      </Fragment>
    );
  };

  const getResourceActions = (resource) => {
    const { resourceId, assessmentId, displayName } = resource;
    const sppAssignments = getSppAssignments(resourceId, assessmentId);

    let buttonText = '';

    if (get(sppAssignments, 'length', 0) === 0) {
      return null;
    }

    if (get(sppAssignments, 'length', 0) === 1) {
      const assignment = sppAssignments[0];
      const learnosityActivityId = get(assignment, 'learnosityActivityId', '');
      const isSPPSandbox = (learnosityActivityId || '').startsWith(
        'SPP_SANDBOX',
      );

      const score = get(assignment, 'score', 0) || 0;
      const maxScore = get(assignment, 'maxScore', 0) || 0;
      const decimalScore = maxScore ? score / maxScore : 0;
      const isFullyAnsweredSandbox =
        parseInt(decimalScore, 10) === 1 && isSPPSandbox;

      /**
       * For the Sandbox add a 'Practice' suffix to the button label to reenforce the
       * difference between opening a teacher assigned vs a sandbox assignment.
       * If we have one assignment, it should be the sandbox assignment anyway but we make sure still.
       */
      buttonText = isFullyAnsweredSandbox
        ? 'Review'
        : `${get(assignment, 'headline', '')}${
            isSPPSandbox ? ' Practice' : ''
          }`;

      return (
        <Button
          isPrimary
          tabIndex={0}
          accessibility={{ 'aria-label': buttonText }}
          onClick={(event) =>
            onResourceClicked(event, getAssignmentUrl(assignment))
          }
          ref={(element) => (actionButtonRef.current[assessmentId] = element)}
        >
          {buttonText}
        </Button>
      );
    }

    const sortedByCreatedAt = sppAssignments.sort(
      (
        { assignmentCreatedAt: assignmentCreatedAt_A },
        { assignmentCreatedAt: assignmentCreatedAt_B },
      ) => {
        if (dayjs(assignmentCreatedAt_A).isBefore(assignmentCreatedAt_B)) {
          return -1;
        }

        if (dayjs(assignmentCreatedAt_B).isBefore(assignmentCreatedAt_A)) {
          return 1;
        }

        return 0;
      },
    );

    return (
      <StudentViewAll
        header={displayName}
        assessmentId={assessmentId}
        assignments={sortedByCreatedAt}
        getSingleAssignmentAction={getSingleAssignmentAction}
        actionButtonRef={actionButtonRef}
      />
    );
  };

  const onResourceClicked = (event, url, ref) => {
    event.stopPropagation();
    ref?.click?.();
    url &&
      router.push({
        pathname: url,
        state: {
          returnPath: `${location.pathname}${location.search}`,
        },
      });
  };

  return (
    <PracticeProblems
      getResourceActions={getResourceActions}
      resources={resources}
      getIcons={getIcons}
      showResourceContent
      showRightContent
      getSppAssignments={getSppAssignments}
      onTitleClicked={(event, resource, assessmentId) => {
        // ref holds popover pointer
        if (actionButtonRef.current[assessmentId]?.open) {
          return actionButtonRef.current[assessmentId].open();
        }
        return actionButtonRef.current[assessmentId]?.click();
      }}
    />
  );
};

StudentPracticeProblems.propTypes = {
  unitId: PropTypes.string.isRequired,
  resources: PropTypes.array.isRequired,
};

const mapStateToProps = ({ userData, featureFlag }) => ({
  userData,
  featureFlag,
});

export default compose(
  withRouter,
  connect(mapStateToProps),
  connectLean({
    scope: 'units',
    mapState(state) {
      return {
        unitsStatuses: {
          ...state,
        },
      };
    },
  }),
)(StudentPracticeProblems);
